import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from '../../hooks/use-toast';
import { ArrowLeft, KeyRound, Shield } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PortalClockPin = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('portal_token');
  const headers = { Authorization: `Bearer ${token}` };

  const [hasPin, setHasPin] = useState(false);
  const [current, setCurrent] = useState('');
  const [pin, setPin] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/clock/pin/status`, { headers })
      .then(r => setHasPin(!!r.data?.has_pin))
      .catch(e => { if (e?.response?.status === 401) navigate('/employee-portal/login'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onlyDigits = (v) => v.replace(/\D/g, '').slice(0, 6);

  const save = async (e) => {
    e.preventDefault();
    if (!/^\d{4,6}$/.test(pin)) { toast({ title: 'PIN must be 4 to 6 digits', variant: 'destructive' }); return; }
    if (pin !== confirm) { toast({ title: 'PINs do not match', variant: 'destructive' }); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/clock/pin`, { pin, current_pin: hasPin ? current : undefined }, { headers });
      toast({ title: hasPin ? 'PIN updated' : 'PIN set successfully' });
      setHasPin(true);
      setCurrent(''); setPin(''); setConfirm('');
    } catch (err) {
      toast({ title: 'Failed', description: err?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-center text-gray-500">Loading…</div>;

  return (
    <div className="max-w-md mx-auto space-y-4" data-testid="portal-clock-pin">
      <button onClick={() => navigate('/employee-portal')} className="flex items-center gap-1 text-sm text-violet-600">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="flex items-center gap-3">
        <div className="h-12 w-12 rounded-full bg-violet-100 flex items-center justify-center">
          <KeyRound className="h-6 w-6 text-violet-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold">Clock-In PIN</h1>
          <p className="text-xs text-gray-500">4-6 digit code used when clocking in/out at the station QR</p>
        </div>
      </div>

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2 bg-violet-50 text-violet-800 text-xs p-2 rounded-md">
            <Shield className="h-4 w-4 flex-shrink-0" />
            <span>Your PIN is stored securely (hashed) and is separate from your portal password.</span>
          </div>

          <form onSubmit={save} className="space-y-3">
            {hasPin && (
              <div>
                <Label>Current PIN</Label>
                <Input
                  type="password"
                  inputMode="numeric"
                  value={current}
                  onChange={e => setCurrent(onlyDigits(e.target.value))}
                  required
                  data-testid="pin-current"
                />
              </div>
            )}
            <div>
              <Label>{hasPin ? 'New PIN' : 'Set PIN'}</Label>
              <Input
                type="password"
                inputMode="numeric"
                value={pin}
                onChange={e => setPin(onlyDigits(e.target.value))}
                placeholder="4 to 6 digits"
                required
                data-testid="pin-new"
              />
            </div>
            <div>
              <Label>Confirm</Label>
              <Input
                type="password"
                inputMode="numeric"
                value={confirm}
                onChange={e => setConfirm(onlyDigits(e.target.value))}
                required
                data-testid="pin-confirm"
              />
            </div>
            <Button type="submit" disabled={saving} className="w-full bg-violet-600 hover:bg-violet-700" data-testid="pin-save">
              {saving ? 'Saving…' : (hasPin ? 'Update PIN' : 'Set PIN')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PortalClockPin;
